import { Box, Button, Skeleton, Stack } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router"
import useSwr from "swr"
import { NetworkApi } from "../../apis"
import { PageLayout } from "../../components/layout"
import { usePermission } from "../../components/permission"
import { SupplierCustomerDetailForm } from "../../components/supplier-customer/detail-form"
import { ListState } from "../../components/ui"

/** Customer detail with load, error, not-found and denied states. */
export function SupplierCustomerDetail() {
  const params = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation(["supplierSales", "common"])
  const ORD_NET_DETAIL = usePermission("ORD_NET_DETAIL")
  const ORD_NET_LIST = usePermission("ORD_NET_LIST")
  // BE network/get.ts: any of ORD_NET_DETAIL / ORD_NET_LIST / ORD_SAVE / ORD_SEND / ORD_EDIT.
  const allowed = ORD_NET_DETAIL.hasAccess || ORD_NET_LIST.hasAccess

  const { data, error, isLoading, mutate } = useSwr(
    allowed ? `/ord/network/detail.${params.id}` : null,
    async () => await NetworkApi.get(params.id as string),
  )

  const frame = (children: React.ReactNode) => (
    <PageLayout
      title={t("supplierSales:customer.detailTitle")}
      breadcrumb={[
        { to: "/", label: t("common:breadcrumb.home") },
        { to: "/supplier-customer", label: t("supplierSales:customer.title") },
        { label: t("supplierSales:customer.detailTitle") },
      ]}>
      <Box>{children}</Box>
    </PageLayout>
  )

  if (!allowed) return frame(ORD_NET_DETAIL.accessDenied())
  if (isLoading && !data) {
    return frame(
      <Stack gap="md">
        <Skeleton height={200} radius="lg" />
        <Skeleton height={260} radius="lg" />
      </Stack>,
    )
  }
  const status = (error as { statusCode?: number } | undefined)?.statusCode
  if (status === 404 || (!error && !data)) {
    return frame(
      <ListState
        state="empty"
        title={t("supplierSales:customer.notFoundTitle")}
        description={t("supplierSales:customer.notFoundText")}
        action={{ label: t("supplierSales:customer.backToList"), onClick: () => navigate("/supplier-customer") }}
      />,
    )
  }
  if (error) {
    return frame(
      <Stack gap="md" align="flex-start">
        <ListState state="error" onRetry={() => mutate()} errorCode={status} />
        <Button variant="default" onClick={() => navigate("/supplier-customer")}>{t("supplierSales:customer.backToList")}</Button>
      </Stack>,
    )
  }

  return <SupplierCustomerDetailForm action={["detail", data]} />
}
